import axios from 'axios';
import config from './config';

// Environment-based URL configuration
const getBaseURL = () => {
  const envUrl = process.env.VUE_APP_URL_HTTP;
  const fallbackUrl = 'https://www.alareeef.com/AlearifAPIs_1/APIs/';
  
  // Always prioritize environment variable if set
  if (envUrl && envUrl !== 'undefined') {
    return envUrl;
  }
  
  // Only use proxy in development if no env URL is set
  if (process.env.NODE_ENV === 'development') {
    return '/api/';
  }
  
  return fallbackUrl;
};

// Create axios instance with enhanced configuration
export const HTTP = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30 seconds timeout
  withCredentials: false, // Prevent CORS cookie issues
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Request interceptor for adding authentication and logging
HTTP.interceptors.request.use(
  (requestConfig) => {
    // Add authorization header if available
    if (config.authHeader) {
      requestConfig.headers.Authorization = config.authHeader;
    }
    
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with better error handling
HTTP.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle different types of errors
    if (axios.isCancel(error)) {
      // Handle canceled requests
      error.isAbort = true;
      return Promise.resolve({ data: null, canceled: true });
    }
    
    if (error.code === 'ERR_NETWORK') {
      // Network errors (no internet, server down)
      error.isNetworkError = true;
    } else if (error.code === 'ECONNABORTED') {
      // Timeout errors
      error.isTimeoutError = true;
    } else if (error.response) {
      // Server responded with error status
      const { status } = error.response;
      
      // Handle specific status codes
      switch (status) {
        case 401:
          error.isAuthError = true;
          break;
        case 403:
          error.isForbiddenError = true;
          break;
        case 404:
          error.isNotFoundError = true;
          break;
        case 500:
          error.isServerError = true;
          break;
      }
    }
    
    // Always reject the error for proper error handling downstream
    return Promise.reject(error);
  }
);

// Helper functions for common HTTP operations
export const httpHelpers = {
  /**
   * GET request with error handling
   * @param {string} url - The endpoint URL
   * @param {object} params - Query parameters
   * @param {object} options - Additional axios options
   */
  async get(url, params = {}, options = {}) {
    try {
      const response = await HTTP.get(url, { params, ...options });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * POST request with error handling
   * @param {string} url - The endpoint URL
   * @param {object} data - Request body data
   * @param {object} options - Additional axios options
   */
  async post(url, data = {}, options = {}) {
    try {
      const response = await HTTP.post(url, data, options);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * PUT request with error handling
   * @param {string} url - The endpoint URL
   * @param {object} data - Request body data
   * @param {object} options - Additional axios options
   */
  async put(url, data = {}, options = {}) {
    try {
      const response = await HTTP.put(url, data, options);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * DELETE request with error handling
   * @param {string} url - The endpoint URL
   * @param {object} options - Additional axios options
   */
  async delete(url, options = {}) {
    try {
      const response = await HTTP.delete(url, options);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Enhanced error handler
   * @param {Error} error - The error object
   */
  handleError(error) {
    // Return a standardized error object
    return {
      message: error.message || 'An unknown error occurred',
      status: error.response?.status,
      data: error.response?.data,
      isNetworkError: error.isNetworkError || false,
      isTimeoutError: error.isTimeoutError || false,
      isAuthError: error.isAuthError || false,
      isForbiddenError: error.isForbiddenError || false,
      isNotFoundError: error.isNotFoundError || false,
      isServerError: error.isServerError || false,
      isAbort: error.isAbort || false,
      originalError: error
    };
  }
};

export default HTTP;